import { AxiosInstance } from "axios";
import { Section } from "../types/Section";
import { DynamicField, normalizeDynamicField } from "../types/DynamicField";
import { FormulairePayload } from "../types/FormulairePayload";
import { Formulaire, transformFormulairePayload } from "../types/Formulaire";

export function buildFormulairePayload(
  titre: string,
  sections: Section[],
  fields: DynamicField[],
  fenetreId: number,
  sousFenetreId: number
): FormulairePayload {
  fields = fields.map((field) => {
    return normalizeDynamicField(field);
  });

  const fieldIdToIndexMap = new Map<string, number>();
  fields.forEach((field, index) => {
    fieldIdToIndexMap.set(field.id, index);
  });

  const sectionsMap = new Map<string, DynamicField[]>();
  fields.forEach((field) => {
    if (!field.section) return;

    const secId = field.section.id;
    if (!sectionsMap.has(secId)) {
      sectionsMap.set(secId, []);
    }
    sectionsMap.get(secId)!.push(field);
  });

  const sectionContenus = sections.map((section) => {
    const sectionFields = sectionsMap.get(section.id) ?? [];

    const sectionObject = {
      nomSection: section.name,
      fenetre: fenetreId !== 0 ? {
        idFenetre: fenetreId,
        tblName: "",
        titre: "",
        menu: null,
      } : null,
      sousFenetre: sousFenetreId !== 0 ? {
        idSousFenetre: sousFenetreId,
        nomSousFenetre: "",
        tblName: "",
        titre: "",
        fenetre: null,
      } : null
    };

    return {
      section: sectionObject,
      contenus: sectionFields.map((f) => {
        let conditionalLogic = null;
        
        if (f.conditionalLogic) {
          const parentIndex = fieldIdToIndexMap.get(f.conditionalLogic.fieldId);
          
          conditionalLogic = {
            parentFieldIndex: parentIndex !== undefined ? parentIndex : -1,
            parentFieldId: f.conditionalLogic.fieldId,
            operator: f.conditionalLogic.operator,
            value: typeof f.conditionalLogic.value === 'string' 
              ? f.conditionalLogic.value 
              : JSON.stringify(f.conditionalLogic.value)
          };
        }

        return {
          nomContenu: f.label,
          typeBase: f.dbType.id,
          typeChamp: f.type.id,
          valueSelect: f.options ? f.options.join(",") : null,
          section: sectionObject,
          active: true,
          mandatory: true,
          conditionalLogic: conditionalLogic
        };
      }),
    };
  });

  return {
    titre,
    sectionContenus,
  };
}

const saveFormulaireFenetre = async (
  api: AxiosInstance,
  idFenetre: number,
  payload: FormulairePayload,
): Promise<any> => {
  try {
    const response = await api.post("/formulaire/fenetre/" + idFenetre, payload);
    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'enregistrement du formulaire :", error);
    throw error;
  }
};

const saveFormulaireSousFenetre = async (
  api: AxiosInstance,
  idSousFenetre: number,
  payload: FormulairePayload
): Promise<any> => {
  try {
    const response = await api.post("/formulaire/sous-fenetre/" + idSousFenetre, payload);
    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'enregistrement du formulaire :", error);
    throw error;
  }
};

const getFormulaireFenetre = async (
  api: AxiosInstance,
  idFenetre: number
): Promise<Formulaire> => {
  try {
    const response = await api.get(`/formulaire/fenetre/${idFenetre}`);
    return transformFormulairePayload(response.data);
  } catch (error) {
    console.error("Erreur lors de la récupération du formulaire de la fenêtre :", error);
    throw error;
  }
};

const getFormulaireSousFenetre = async (
  api: AxiosInstance,
  idSousFenetre: number
): Promise<Formulaire> => {
  try {
    const response = await api.get(`/formulaire/sous-fenetre/${idSousFenetre}`);
    return transformFormulairePayload(response.data);
  } catch (error) {
    console.error("Erreur lors de la récupération du formulaire de la sous-fenêtre :", error);
    throw error;
  }
};

export const FormulaireService = {
  saveFormulaire: async (
    api: AxiosInstance,
    idFenetre: number,
    idSousFenetre: number,
    payload: FormulairePayload
  ): Promise<any> => {
    if (idFenetre !== 0) {
      return saveFormulaireFenetre(api, idFenetre, payload);
    } else if (idSousFenetre !== 0) {
      return saveFormulaireSousFenetre(api, idSousFenetre, payload);
    } else {
      throw Error("Veuillez fournir un idFenetre ou un idSousFenetre");
    }
  },

  getFormulaire: async (
    api: AxiosInstance,
    idFenetre: number,
    idSousFenetre: number
  ): Promise<Formulaire> => {
    if (idFenetre !== 0) {
      return getFormulaireFenetre(api, idFenetre);
    } else if (idSousFenetre !== 0) {
      return getFormulaireSousFenetre(api, idSousFenetre);
    } else {
      throw Error("Veuillez fournir un idFenetre ou un idSousFenetre");
    }
  },
};